<template>
  <transition name="app-toast-fade">
    <div
      v-if="value"
      class="app-toast-host"
      :class="`app-toast-host--${position}`"
      role="status"
      aria-live="polite"
    >
      <div class="app-toast" :class="`app-toast--${variant}`">
        <div class="app-toast__body">
          <i
            v-if="showCheckIcon"
            class="ri-checkbox-circle-fill app-toast__icon"
          ></i>
          <i
            v-else-if="variant === 'error'"
            class="ri-error-warning-fill app-toast__icon"
          ></i>

          <span class="app-toast__message">{{ message }}</span>

          <button
            v-if="actionText"
            type="button"
            class="app-toast__action"
            @click="onAction"
          >
            {{ actionText }}
          </button>
        </div>

        <button
          type="button"
          class="app-toast__close"
          aria-label="Close"
          @click="close"
        >
          <i class="ri-close-line"></i>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
const POSITIONS = [
  'top-left',
  'top-center',
  'top-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

export default {
  name: 'AppToast',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'success',
      validator: (v) => ['success', 'error', 'info'].includes(v),
    },
    message: {
      type: String,
      default: '',
    },
    actionText: {
      type: String,
      default: '',
    },
    timeout: {
      type: Number,
      default: 4000,
    },
    position: {
      type: String,
      default: 'bottom-center',
      validator: (v) => POSITIONS.includes(v),
    },
  },
  data() {
    return {
      dismissTimer: null,
    };
  },
  computed: {
    showCheckIcon() {
      if (this.variant === 'success') return true;
      if (this.variant === 'info' && !this.actionText) return true;
      return false;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(isVisible) {
        this.clearTimer();
        if (isVisible && this.timeout > 0) {
          this.dismissTimer = setTimeout(() => this.close(), this.timeout);
        }
      },
    },
  },
  beforeDestroy() {
    this.clearTimer();
  },
  methods: {
    clearTimer() {
      if (this.dismissTimer) {
        clearTimeout(this.dismissTimer);
        this.dismissTimer = null;
      }
    },
    close() {
      this.clearTimer();
      this.$emit('input', false);
      this.$emit('close');
    },
    onAction() {
      this.$emit('action');
    },
  },
};
</script>

<style scoped>
.app-toast-host {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
}

.app-toast-host--top-left {
  top: 24px;
  left: 24px;
}

.app-toast-host--top-center {
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
}

.app-toast-host--top-right {
  top: 24px;
  right: 24px;
}

.app-toast-host--bottom-left {
  bottom: 32px;
  left: 24px;
}

.app-toast-host--bottom-center {
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
}

.app-toast-host--bottom-right {
  bottom: 32px;
  right: 24px;
}

.app-toast {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: auto;
  min-width: 320px;
  max-width: min(460px, calc(100vw - 48px));
  padding: 10px 12px 10px 16px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(102, 102, 102, 0.2);
  pointer-events: auto;
  box-sizing: border-box;
}

.app-toast--success {
  background: #33b273;
}

.app-toast--error {
  background: #c53030;
}

.app-toast--info {
  background: #5b7edb;
}

.app-toast__body {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 2px 0;
}

.app-toast__icon {
  font-size: 18px;
  color: #ffffff;
  flex-shrink: 0;
}

.app-toast__message {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: #ffffff;
  flex: 1;
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
}

.app-toast__action {
  border: none;
  background: transparent;
  padding: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  color: #ffffff;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
}

.app-toast__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  flex-shrink: 0;
}

.app-toast__close:hover {
  background: rgba(255, 255, 255, 0.12);
}

.app-toast__close i {
  font-size: 18px;
}

.app-toast-fade-enter-active,
.app-toast-fade-leave-active {
  transition: opacity 0.2s ease;
}

.app-toast-fade-enter,
.app-toast-fade-leave-to {
  opacity: 0;
}
</style>
